import { IRequest, RequestStatus, RequestType } from './types';
import { PERMISSION } from '../../../components/investigation/share/user_roles';

/**
 * SHER-366 — gating policy for the modify/cancel-request UI controls.
 *
 * Pure predicates mirroring the backend guards so a control is only offered
 * when the API would accept the action. The server remains the source of truth;
 * these just keep the UI from presenting obviously-blocked actions.
 */

// A request is closed (no in-place mutation / cancel) in any of these states.
export const REQUEST_TERMINAL_STATUSES = [
    RequestStatus.COMPLETED,
    RequestStatus.CANCELED,
    RequestStatus.CANCELED_BY_USER,
    RequestStatus.DENIED,
    RequestStatus.EXPIRED,
];

export function isRequestTerminal(request: IRequest): boolean {
    return REQUEST_TERMINAL_STATUSES.includes(request.status);
}

// Mirrors cancelRequest: staff CANCEL_REQUESTS permission, not a surgery
// (cancellation is owned by the surgery workflow), and the request still open.
export function canCancelRequest(request: IRequest, permissions: string[]): boolean {
    return permissions.includes(PERMISSION.CANCEL_REQUESTS)
        && request.type !== RequestType.SURGERY
        && !isRequestTerminal(request);
}

// Mirrors the add/remove-service guards: MODIFY_REQUESTS, request still open, and
// — for surgery — not past check-in. (Paid-item removal is blocked server-side;
// the client item shape carries no payment info, so the API surfaces that error.)
export function canModifyRequestServices(request: IRequest, permissions: string[]): boolean {
    return permissions.includes(PERMISSION.MODIFY_REQUESTS)
        && !isRequestTerminal(request)
        && !(request.type === RequestType.SURGERY && request.status === RequestStatus.CHECKED_IN);
}

// A service item can be removed only when more than one remains (the last service
// must be removed by cancelling the request instead — matches the backend guard).
export function canRemoveServiceItem(request: IRequest, permissions: string[]): boolean {
    return canModifyRequestServices(request, permissions)
        && (request.requestsServiceInvestigation?.length ?? 0) > 1;
}
